import { isPlatformBrowser } from '@angular/common';
import { APP_INITIALIZER, ApplicationConfig, isDevMode, PLATFORM_ID, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  AURA_UI_BASE_PATH,
  AURA_UI_CHAT_AUTH,
  provideAuraApi,
  provideAuraMarkdown,
} from '@powerfleet/aura_standalone.ui';
import { provideTransloco, translocoConfig } from '@jsverse/transloco';
import { WindowWrapper } from '@powerfleet/shared/common/interfaces';
import { ROOT_PATH } from '@powerfleet/shared/injection-tokens';
import { provideApi } from '../api';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from './transloco-loader';
import { IconCacheService } from '@powerfleet/shared/utils';
import { SharedBroadcastService } from '@powerfleet/shared/services/common/shared-broadcast';
import { IconSettingsService } from '@progress/kendo-angular-icons';
import { environment } from '../environments/environment';
import { keycloakAuthInterceptor } from './auth/keycloak-auth.interceptor';
import { KeycloakAuthService } from './auth/keycloak-auth.service';
import { AuraChatAuthService } from './auth/aura-chat-auth.service';
import { KendoIconsService } from './shared/kendo-icons.service';
import { HostApplicationService } from './services/host-application.service';

function initialiseKeycloak(keycloakAuthService: KeycloakAuthService): () => Promise<void> {
  return () => keycloakAuthService.initialise();
}

function initialiseKendoIcons(kendoIconsService: KendoIconsService): () => Promise<void> {
  return () => kendoIconsService.init();
}

/**
 * Initialise embedded mode authentication from host application.
 * In local development with a host app, this retrieves the auth token
 * that will be passed to the API instead of using Keycloak.
 */
function initialiseHostApplication(hostApplicationService: HostApplicationService): () => Promise<void> {
  return () => hostApplicationService.initialiseEmbeddedMode();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(routes),
    provideHttpClient(withFetch(), withInterceptors([keycloakAuthInterceptor])),
    provideApi(environment.apiBaseUrl),
    // Aura's internal API client resolves its base URL from AURA_UI_BASE_PATH (separate origin).
    provideAuraApi(),
    { provide: AURA_UI_BASE_PATH, useValue: environment.aura.apiBaseUrl },
    // Bridge the app's Keycloak auth to the package's Fetch-based streaming auth.
    { provide: AURA_UI_CHAT_AUTH, useExisting: AuraChatAuthService },
    provideAuraMarkdown(),
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en-US'],
        defaultLang: 'en-US',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      }),
      loader: TranslocoHttpLoader,
    }),
    provideClientHydration(withEventReplay()),
    {
      provide: WindowWrapper,
      useFactory: (platformId: Object): WindowWrapper => (
        isPlatformBrowser(platformId) ? (window as WindowWrapper) : ({} as WindowWrapper)
      ),
      deps: [PLATFORM_ID],
    },
    { provide: ROOT_PATH, useFactory: () => '/' },
    IconCacheService,
    KendoIconsService,
    {
      provide: IconSettingsService,
      useExisting: KendoIconsService,
    },
    SharedBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: initialiseHostApplication,
      deps: [HostApplicationService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialiseKeycloak,
      deps: [KeycloakAuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initialiseKendoIcons,
      deps: [KendoIconsService],
      multi: true,
    },
  ]
};
